import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";

export default function SingleDatePicker({
  value,
  onChange,
  label,
  placeholder = "Select date",
  error,
  minDate,
  maxDate,
  disabled = false,
  name,
  infoText,
  showInfo = false,
  useNativeMobile = false, // New prop to enable native date picker on mobile
}) {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";
  const [currentDate, setCurrentDate] = useState(() => {
    // If there's a value, use it for the initial calendar date
    if (value) {
      return new Date(value);
    }
    // If there's a maxDate and it's in the past (birth date pickers), start the calendar there
    if (maxDate) {
      const today = new Date();
      const maxDateObj = new Date(maxDate);
      // Only use maxDate as initial view if it's in the past (birth date scenario)
      if (maxDateObj < today) {
        return maxDateObj;
      }
    }
    // Default to today for future date pickers or when no specific date context
    return new Date();
  });
  const [selectedDate, setSelectedDate] = useState(
    value ? new Date(value) : null
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState("bottom");
  const [calendarCoords, setCalendarCoords] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const inputRef = useRef(null);
  const calendarRef = useRef(null);

  const monthNames = [
    translate("January", "يناير"),
    translate("February", "فبراير"),
    translate("March", "مارس"),
    translate("April", "أبريل"),
    translate("May", "مايو"),
    translate("June", "يونيو"),
    translate("July", "يوليو"),
    translate("August", "أغسطس"),
    translate("September", "سبتمبر"),
    translate("October", "أكتوبر"),
    translate("November", "نوفمبر"),
    translate("December", "ديسمبر"),
  ];

  const dayNames = [
    translate("M", "الإثنين"), // Monday
    translate("T", "الثلاثاء"), // Tuesday
    translate("W", "الأربعاء"), // Wednesday
    translate("T", "الخميس"), // Thursday
    translate("F", "الجمعة"), // Friday
    translate("S", "السبت"), // Saturday
    translate("S", "الأحد"), // Sunday
  ];

  // Update currentDate when value or maxDate changes
  useEffect(() => {
    if (value) {
      // If there's a value, set the calendar to show that date's month
      setCurrentDate(new Date(value));
    } else if (maxDate) {
      // If there's no value but there's a maxDate and it's in the past, use maxDate for birth date pickers
      const today = new Date();
      const maxDateObj = new Date(maxDate);
      // Only use maxDate as initial view if it's in the past (birth date scenario)
      if (maxDateObj < today) {
        setCurrentDate(maxDateObj);
      } else {
        // For future date pickers, keep showing current month
        setCurrentDate(new Date());
      }
    }
  }, [value, maxDate]);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      // Recalculate calendar position on resize if calendar is open
      if (isOpen) {
        calculateCalendarPosition();
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, [isOpen]);

  // Reposition the portaled calendar on scroll/resize while it is open.
  // The calendar is rendered into document.body with fixed positioning so its
  // z-index is not trapped inside transformed ancestors (e.g. animated page
  // wrappers) that would otherwise let the sticky footer cover it.
  useEffect(() => {
    if (!isOpen) return;
    const handleReposition = () => calculateCalendarPosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [isOpen]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Prevent body scroll on mobile when calendar is open
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobile, isOpen]);

  // Update selected date when value prop changes
  useEffect(() => {
    setSelectedDate(value ? new Date(value) : null);
  }, [value]);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1;
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.toDateString() === date2.toDateString();
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    // Check minDate constraint first
    if (minDate) {
      const minDateNormalized = new Date(minDate);
      minDateNormalized.setHours(0, 0, 0, 0);
      if (checkDate < minDateNormalized) return true;
    }

    // Check maxDate constraint
    if (maxDate) {
      const maxDateNormalized = new Date(maxDate);
      maxDateNormalized.setHours(0, 0, 0, 0);
      if (checkDate > maxDateNormalized) return true;
    }

    // Only disable future dates if no maxDate is provided (for birth date pickers, maxDate is in the past)
    if (!maxDate && checkDate > today) return true;

    // Only disable past dates if maxDate is in the future (for future date pickers)
    if (maxDate) {
      const maxDateNormalized = new Date(maxDate);
      maxDateNormalized.setHours(0, 0, 0, 0);
      // If maxDate is in the future, then disable past dates
      if (maxDateNormalized > today && checkDate < today) return true;
    } else {
      // No maxDate specified, disable past dates by default
      if (checkDate < today) return true;
    }

    return false;
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date) || disabled) {
      return;
    }

    setSelectedDate(date);
    if (onChange) {
      // Call onChange with the date value in ISO format for consistency
      onChange(date.toISOString());
    }
    setIsOpen(false);
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
      // Calculate position after the calendar is rendered
      setTimeout(() => {
        calculateCalendarPosition();
      }, 0);
    }
  };

  const calculateCalendarPosition = () => {
    if (!inputRef.current) return;

    const inputRect = inputRef.current.getBoundingClientRect();
    const calendarHeight = 300; // Estimated calendar height without max-height restriction
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;

    // Show below if there's enough space (with 20px buffer), otherwise show above
    let position = "bottom";
    if (spaceBelow >= calendarHeight + 20) {
      position = "bottom";
    } else if (spaceAbove >= calendarHeight + 20) {
      position = "top";
    } else {
      // If neither has enough space, choose the side with more space
      position = spaceBelow > spaceAbove ? "bottom" : "top";
    }
    setCalendarPosition(position);

    // Clamp horizontally so the body-level fixed dropdown never overflows the
    // viewport (it is at least 280px wide).
    const calendarWidth = Math.max(inputRect.width, 280);
    const viewportWidth = window.innerWidth;
    const clampedLeft = Math.min(
      Math.max(inputRect.left, 8),
      Math.max(viewportWidth - calendarWidth - 8, 8)
    );

    setCalendarCoords({
      top: position === "top" ? inputRect.top : inputRect.bottom,
      left: clampedLeft,
      width: inputRect.width,
    });
  };

  const navigateMonth = (direction) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (direction === "prev") {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const renderMonth = (monthOffset = 0) => {
    const displayDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + monthOffset,
      1
    );
    const daysInMonth = getDaysInMonth(displayDate);
    const firstDayOfMonth = getFirstDayOfMonth(displayDate);

    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(
        <div
          key={`empty-${i}`}
          className={`${isMobile ? "h-8 w-8" : "h-10 w-10"} m-0.5`}
        />
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        displayDate.getFullYear(),
        displayDate.getMonth(),
        day
      );
      const isSelected = isSameDay(date, selectedDate);
      const isDisabled = isDateDisabled(date);

      let dayClasses = `${
        isMobile
          ? "h-8 w-8 text-xs min-h-[32px] min-w-[32px]"
          : "h-10 w-10 text-sm"
      } flex items-center justify-center font-medium cursor-pointer transition-colors relative m-0.5 rounded-md`;

      if (isDisabled) {
        dayClasses += " text-gray-300 cursor-not-allowed";
      } else if (isSelected) {
        dayClasses += " bg-[#00719A] text-white";
      } else {
        dayClasses += " text-[#000] hover:bg-gray-100";
      }

      days.push(
        <div
          key={day}
          className={dayClasses}
          onClick={() => handleDateClick(date)}
        >
          <span className="relative z-10">{day}</span>
        </div>
      );
    }

    return (
      <div className="flex-1">
        <div className={`grid grid-cols-7 ${isRTL ? "mb-0" : "mb-2"}`}>
          {dayNames.map((day, index) => (
            <div
              key={index}
              className={`${
                isMobile ? "h-8 w-8" : "h-10 w-10"
              } flex items-center justify-center text-gray-500 ${
                isRTL ? "text-[9px] font-medium" : "font-medium"
              }`}
            >
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">{days}</div>
      </div>
    );
  };

  const formatDate = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    // Format as DD/MM/YYYY for both English and Arabic
    return `${day}/${month}/${year}`;
  };

  // Convert value to HTML5 date format (YYYY-MM-DD)
  const getHtml5DateValue = () => {
    if (!value) return "";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // Handle native date input change
  const handleNativeDateChange = (e) => {
    const dateValue = e.target.value; // YYYY-MM-DD format
    if (dateValue && onChange) {
      onChange(dateValue);
    }
  };

  return (
    <div className="w-full form-input" dir={isRTL ? "rtl" : "ltr"}>
      {/* Label */}
      {label && (
        <label className="label text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px] flex items-center gap-2 mb-[5px]">
          {label}
          {showInfo && infoText && (
            <div className="relative group">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="cursor-pointer text-[#5B6871]"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="7"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M8 7V11M8 5V5.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 min-w-[200px] text-center">
                {infoText}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          )}
        </label>
      )}

      {/* Input Field */}
      <div className="relative">
        {/* Mobile Native Date Input with Calendar Icon */}
        {isMobile && useNativeMobile ? (
          <div className="relative">
            <input
              type="date"
              value={getHtml5DateValue()}
              onChange={handleNativeDateChange}
              min={minDate}
              max={maxDate}
              disabled={disabled}
              className={`w-full h-[50px] px-4 py-3 pr-12 border rounded-[10px] bg-white text-base transition-colors border-[#B0BABF] focus:border-[#0891B2] ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
              style={{
                fontSize: "16px", // Prevent zoom on iOS
                WebkitAppearance: "none",
                appearance: "none",
              }}
            />
            {/* Calendar Icon - positioned like other date pickers */}
            <Calendar
              size={20}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
            />
          </div>
        ) : (
          // Custom Calendar Input (Desktop or when useNativeMobile is false)
          <div
            ref={inputRef}
            onClick={handleInputClick}
            className={`w-full h-[50px] px-4 py-3 border rounded-[10px] bg-white cursor-pointer flex items-center justify-between hover:border-gray-400 transition-colors ${
              isOpen
                ? "border-[#0891B2] ring-1 ring-[#0891B2]"
                : error
                ? "border-[#B0BABF]"
                : "border-[#B0BABF]"
            } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
          >
            <span
              className={`${isMobile ? "text-base" : "text-lg"} font-normal ${
                selectedDate ? "text-[#000]" : "text-gray-500"
              }`}
            >
              {formatDate(selectedDate) || placeholder}
            </span>
            <Calendar size={20} className="text-gray-400" />
          </div>
        )}

        {/* Calendar - Only show when not using native mobile.
            Rendered in a portal with fixed positioning so it escapes any
            transformed ancestor and stays above sticky/portaled footers. */}
        {isOpen &&
          !disabled &&
          !(isMobile && useNativeMobile) &&
          createPortal(
            <div
              ref={calendarRef}
              dir={isRTL ? "rtl" : "ltr"}
              style={{
                position: "fixed",
                top: calendarCoords.top,
                left: calendarCoords.left,
                minWidth: Math.max(calendarCoords.width || 0, 280),
                transform:
                  calendarPosition === "top"
                    ? "translateY(calc(-100% - 8px))"
                    : "translateY(8px)",
              }}
              className="z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg w-auto min-w-[280px]"
            >
            <div className="bg-white border-0 shadow-none rounded-lg">
              <div className={`${isMobile ? "p-3" : "p-4"}`}>
                {/* Navigation header */}
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => navigateMonth("prev")}
                    className="p-1 bg-transparent hover:bg-gray-100 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
                  >
                    {isRTL ? (
                      <ChevronRight size={16} className="text-gray-600" />
                    ) : (
                      <ChevronLeft size={16} className="text-gray-600" />
                    )}
                  </button>

                  <div className="text-base font-semibold text-gray-900">
                    {monthNames[currentDate.getMonth()]}{" "}
                    {currentDate.getFullYear()}
                  </div>

                  <button
                    onClick={() => navigateMonth("next")}
                    className="p-1 bg-transparent hover:bg-gray-100 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
                  >
                    {isRTL ? (
                      <ChevronLeft size={16} className="text-gray-600" />
                    ) : (
                      <ChevronRight size={16} className="text-gray-600" />
                    )}
                  </button>
                </div>

                {/* Calendar Grid - No max height or scroll */}
                <div className="flex justify-center">{renderMonth(0)}</div>
              </div>
            </div>
            </div>,
            document.body
          )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="field-err text-red-500 text-sm mt-1 text-left rtl:text-right">
          {error}
        </p>
      )}
    </div>
  );
}
